import { flexRender, getSortedRowModel } from '@tanstack/react-table'
import { Button, Table } from '$components'
import { SortArrowIcon } from '$components'
import { ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { useTableSort } from '$hooks/use-table-sort'
import { Link } from 'react-router-dom'

export type GenericTableProps<TData> = {
  data: TData[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<TData, any>[]
  rowLink?: (row: TData) => string
}

export function GenericTable<TData>({ data, columns, rowLink }: GenericTableProps<TData>) {
  const { sorting, updateSorting, toggleSort, getSortDirection } = useTableSort()

  const table = useReactTable<TData>({
    data,
    columns,
    state: { sorting },
    onSortingChange: updateSorting,
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div className="overflow-y-auto rounded-surface">
      <Table>
        <Table.Header>
          {table.getHeaderGroups().map(headerGroup => (
            <Table.Row
              key={headerGroup.id}
              type="header"
            >
              {headerGroup.headers.map(header => (
                <Table.Head
                  key={header.id}
                  style={{
                    minWidth: header.column.columnDef.minSize,
                    width: header.getSize(),
                    maxWidth: header.column.columnDef.maxSize,
                  }}
                >
                  <div className="flex items-center gap-stack-tight w-full">
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}

                    {header.column.getCanSort() && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleSort(header.column.id)}
                        className="!p-stack-tight"
                      >
                        <SortArrowIcon
                          className="size-4"
                          direction={getSortDirection(header.column.id)}
                        />
                      </Button>
                    )}
                  </div>
                </Table.Head>
              ))}
            </Table.Row>
          ))}
        </Table.Header>
        <Table.Body>
          {table.getRowModel().rows.map(row => (
            <Table.Row key={row.id}>
              {row.getVisibleCells().map((cell, cellIndex) => (
                <Table.Cell
                  key={cell.id}
                  to={rowLink ? rowLink(row.original) : undefined}
                  as={rowLink ? Link : undefined}
                  isFirstCell={cellIndex === 0}
                  style={{
                    minWidth: cell.column.columnDef.minSize,
                    width: cell.column.getSize(),
                    maxWidth: cell.column.columnDef.maxSize,
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Table.Cell>
              ))}
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </div>
  )
}

GenericTable.Container = Table.Container
